import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import IconSparkles from "@/components/icons/atoms/IconSparkles";
import IconSettings from "@/components/icons/atoms/IconSettings";
import IconChevronDown from "@/components/icons/atoms/IconChevronDown";
import IconChevronRight from "@/components/icons/atoms/IconChevronRight";
import IconCheckCircle from "@/components/icons/atoms/IconCheckCircle";
import IconCalendar from "@/components/icons/atoms/IconCalendar";
import IconLink from "@/components/icons/atoms/IconLink";
import LightPreviewNotice from "./LightPreviewNotice";
import { useOnboarding } from "@/context/OnboardingContext";
import { steps } from "@/data/onboarding.data";
import type { OnboardingStep } from "@/types/onbaording.types";
import { useState, useEffect } from "react";
import { useWebsites } from "@/context/WebsitesContext";
import { Link as RouterLink } from "react-router-dom";

export default function OnboardingSidebar({ highlightCalendar = false, onOpenCalendarDrawer, stepsOverride }: { highlightCalendar?: boolean; onOpenCalendarDrawer?: () => void; stepsOverride?: OnboardingStep[] }) {
  const { state, expandedSteps, setExpandedSteps, stepTexts, setStepTexts } = useOnboarding();
  const { progress, currentStepIndex, currentStepText, step } = state;
  const isSettingsMode = typeof step === 'number' && step >= 2;
  const [isCollapsed, setIsCollapsed] = useState(Number(step) >= 2);
  const [calendarEnabled, setCalendarEnabled] = useState(
    typeof window !== 'undefined' && localStorage.getItem('calendar_enabled') === '1'
  );
  const { currentWebsite, updateWebsite } = useWebsites();
  
  const isIdeasStep = Number(step) >= 2;

  // Listen for calendar enabled changes
  useEffect(() => {
    const handleCalendarEnabledChange = () => {
      setCalendarEnabled(localStorage.getItem('calendar_enabled') === '1');
    };

    window.addEventListener('calendar-enabled-changed', handleCalendarEnabledChange);
    return () => {
      window.removeEventListener('calendar-enabled-changed', handleCalendarEnabledChange);
    };
  }, []);

  return (
    <motion.div
      key="analysis-only"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.5, ease: "easeInOut", opacity: { duration: 0.3 } }}
      className="w-full py-4"
    >
      <div className="w-full mx-auto max-w-5xl">
        <div className={`flex items-center justify-between mb-2 px-2 ${highlightCalendar ? 'bg-brand-green-light rounded-md' : ''}`}>
          {calendarEnabled ? (
            <button
              type="button"
              onClick={() => onOpenCalendarDrawer?.()}
              className="inline-flex h-7 items-center gap-2 rounded-md px-0 py-1 my-1 transition-colors text-foreground/80 hover:text-primary"
            >
              <IconCalendar className="w-4 h-4 text-current" />
              <span className="tracking-tight text-sm font-medium">Planned Content</span>
            </button>
          ) : (
            <div />
          )}
          <div />
        </div>

        <div className="my-3 px-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-foreground/80">
              <motion.div initial={{ scale: 0, rotate: -180 }} animate={{ scale: 1, rotate: 0 }} transition={{ type: "spring", stiffness: 260, damping: 20, delay: 0.2 }}>
                {isIdeasStep ? (
                  <IconSettings className="w-4 h-4 text-current" />
                ) : (
                  <IconSparkles className="w-4 h-4 text-current" />
                )}
              </motion.div>
              <motion.h2 className="tracking-tight text-sm font-medium" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.3 }}>
                {isIdeasStep ? "Website Settings" : "Analyzing your website"}
              </motion.h2>
            </div>
            {isIdeasStep && (
              <button
                onClick={() => setIsCollapsed(!isCollapsed)}
                className="p-1 hover:bg-muted rounded-md transition-colors text-foreground/60"
              >
                <IconChevronDown 
                  className={`w-3 h-3 transition-transform duration-200 ${
                    isCollapsed ? 'rotate-0' : 'rotate-180'
                  }`} 
                />
              </button>
            )}
          </div>
        </div>

        <motion.div
          initial={false}
          animate={{ height: isIdeasStep && isCollapsed ? 0 : "auto" }}
          transition={{ duration: 0.3, ease: "easeInOut" }}
          className="overflow-hidden"
        >
          <div className="mb-6">
            <Progress value={progress} className="mb-2 h-1.5" />
            <div className="flex justify-between items-center">
              <p className="text-xs text-muted-foreground">Step {currentStepIndex + 1} of {(stepsOverride || steps).length}</p>
              <p className="text-xs font-medium text-foreground/70">{Math.round(progress)}%</p>
            </div>
          </div>

          <div className="max-h-[calc(100vh-220px)] overflow-y-auto pb-6">
            <div className="space-y-1.5">
              {(stepsOverride || steps)
                .slice(0, currentStepIndex + 1)
                .reverse()
                .map((step, index) => {
                  // In settings mode (post-analysis), nothing is treated as current
                  const isCurrentStep = !isSettingsMode && index === 0;
                  const isExpanded = expandedSteps[step.id] === true;
                  const stepText = isCurrentStep ? currentStepText : stepTexts[step.id] || "";

                  return (
                    <motion.button
                      key={`${step.id}-${index}`}
                      type="button"
                      initial={index === 0 ? { opacity: 0, y: -20, scale: 0.95 } : false}
                      animate={index === 0 ? { opacity: 1, y: 0, scale: 1 } : false}
                      transition={{ duration: 0.4, ease: "easeOut" }}
                      layout="position"
                      className={`w-full flex items-start gap-2.5 p-2.5 rounded-md transition-all duration-300 text-left ${
                        isCurrentStep 
                          ? "bg-primary/5 border border-primary/10" 
                          : "bg-background/40 hover:bg-background/70 hover:border-border/50"
                      }`}
                      onClick={() => {
                        if (!isCurrentStep) {
                          setExpandedSteps((prev) => ({
                            ...prev,
                            [step.id]: !prev[step.id],
                          }));
                        }
                      }}
                      disabled={isCurrentStep}
                    >
                      <div className="mt-0.5 flex-shrink-0">
                        {!isCurrentStep ? (
                          <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring", stiffness: 300, damping: 20 }}>
                            <IconCheckCircle className="w-4 h-4 text-primary" />
                          </motion.div>
                        ) : (
                          <motion.div animate={isSettingsMode ? {} : { scale: [1, 1.05, 1], rotate: [0, 3, -3, 0] }} transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}>
                            <IconSparkles className="w-4 h-4 text-primary" />
                          </motion.div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between cursor-pointer group">
                          <div className="min-w-0">
                            <motion.p className="font-normal truncate text-sm text-foreground" initial={index === 0 ? { opacity: 0, x: -10 } : false} animate={index === 0 ? { opacity: 1, x: 0 } : false} transition={{ duration: 0.3 }}>
                              {step.name}
                            </motion.p>
                          </div>
                          {!isCurrentStep && (
                            <motion.div className="flex-shrink-0 ml-2">
                              <IconChevronRight className={`w-3 h-3 text-muted-foreground transition-transform ${isExpanded ? 'rotate-90' : 'rotate-0'}`} />
                            </motion.div>
                          )}
                        </div>
                        {isCurrentStep && stepText && (
                          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} transition={{ duration: 0.4, ease: "easeOut" }} className="mt-1.5 text-xs text-foreground/80">
                            {stepText.split("\n").map((line, i) => {
                              if (line.startsWith("/") || line.startsWith("http")) {
                                return (
                                  <motion.div key={i} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.3, delay: i * 0.1 }} className="flex items-center gap-2 text-primary/80 hover:text-primary transition-colors">
                                    <IconLink className="w-4 h-4 flex-shrink-0" />
                                    <span className="truncate">{line}</span>
                                  </motion.div>
                                );
                              }
                              return (
                                <motion.p key={i} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: i * 0.1 }} className={`${i > 0 ? "mt-0.5" : ""} break-words`}>
                                  {line}
                                </motion.p>
                              );
                            })}
                          </motion.div>
                        )}
                        {!isCurrentStep && isExpanded && stepText && (
                          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} transition={{ duration: 0.3 }} className="mt-1.5 text-xs text-foreground/80">
                            {step.id === 3 ? (
                              // Language settings - show language selector
                              <div className="space-y-2">
                                <p className="text-xs text-foreground/80 mb-2">Current language: {localStorage.getItem('website_language') || 'en'}</p>
                                <div className="flex gap-1 flex-wrap">
                                  {['en', 'da', 'de', 'fr', 'es'].map((lang) => (
                                    <Button
                                      key={lang}
                                      variant={(localStorage.getItem('website_language') || 'en') === lang ? "default" : "outline"}
                                      size="sm"
                                      onClick={() => {
                                        localStorage.setItem('website_language', lang);
                                        const websiteInfo = JSON.parse(localStorage.getItem('website_info') || '{}');
                                        websiteInfo.language = lang;
                                        localStorage.setItem('website_info', JSON.stringify(websiteInfo));
                                        // Also persist to database if we have a selected website
                                        const effectiveWebsiteId = currentWebsite?.id || localStorage.getItem('currentWebsiteId');
                                        if (effectiveWebsiteId) {
                                          console.log('OnboardingSidebarChat: Updating website language in DB', { id: effectiveWebsiteId, language: lang });
                                          // Fire and forget; UI already updates via localStorage, DB keeps canonical setting
                                          try {
                                            updateWebsite(effectiveWebsiteId as string, { language: lang });
                                          } catch (err) {
                                            console.warn('OnboardingSidebarChat: Failed to update language in DB (non-blocking)', err);
                                          }
                                        }
                                        // Trigger a re-render by updating stepTexts
                                        setStepTexts((prev) => ({
                                          ...prev,
                                          [step.id]: `Language updated to ${lang.toUpperCase()}`
                                        }));
                                      }}
                                      className="text-xs h-6 px-2"
                                    >
                                      {lang.toUpperCase()}
                                    </Button>
                                  ))}
                                </div>
                              </div>
                            ) : (
                              // Other steps - show regular text
                              stepText.split("\n").map((line, i) => (
                                <motion.p key={i} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: i * 0.1 }} className={`${i > 0 ? "mt-0.5" : ""} break-words`}>
                                  {line}
                                </motion.p>
                              ))
                            )}
                          </motion.div>
                        )}
                      </div>
                    </motion.button>
                  );
                })}
            </div>
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
}


